import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Shield, DollarSign, ArrowLeft, CheckCircle, XCircle } from 'lucide-react';
import { Button } from '../../components/ui/button';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminSubscriptions = () => {
  const navigate = useNavigate();
  const [subscriptions, setSubscriptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(0);
  const [total, setTotal] = useState(0);
  const limit = 20;

  useEffect(() => {
    fetchSubscriptions();
  }, [currentPage]);

  const fetchSubscriptions = async () => {
    try {
      const res = await axios.get(
        `${API}/admin/subscriptions?skip=${currentPage * limit}&limit=${limit}`,
        { withCredentials: true }
      );
      setSubscriptions(res.data.subscriptions);
      setTotal(res.data.total);
    } catch (error) {
      console.error('Error fetching subscriptions:', error);
      if (error.response?.status === 401) {
        navigate('/admin/login');
      }
    } finally {
      setLoading(false);
    }
  };

  const totalPages = Math.ceil(total / limit);
  const activeCount = subscriptions.filter(item => item.subscription.status === 'active').length;
  const totalRevenue = subscriptions.reduce((sum, item) => sum + (item.subscription.amount || 0), 0);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-900">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-500 mx-auto mb-4"></div>
          <p className="text-gray-400">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-900">
      {/* Header */}
      <header className="bg-gray-800 border-b border-gray-700">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              onClick={() => navigate('/admin/dashboard')}
              className="text-gray-400 hover:text-white"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
            <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-red-500 to-orange-500 flex items-center justify-center">
              <Shield className="w-6 h-6 text-white" />
            </div>
            <h1 className="text-xl font-bold text-white">Subscriptions</h1>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* Stats Cards */}
        <div className="grid md:grid-cols-3 gap-6 mb-8">
          <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
            <div className="flex items-center justify-between mb-2">
              <DollarSign className="w-8 h-8 text-green-500" />
            </div>
            <div className="text-3xl font-bold text-white mb-1">{total}</div>
            <div className="text-sm text-gray-400">Total Subscriptions</div>
          </div>

          <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
            <div className="flex items-center justify-between mb-2">
              <CheckCircle className="w-8 h-8 text-blue-500" />
            </div>
            <div className="text-3xl font-bold text-white mb-1">{activeCount}</div>
            <div className="text-sm text-gray-400">Active Subscriptions</div>
          </div>

          <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
            <div className="flex items-center justify-between mb-2">
              <DollarSign className="w-8 h-8 text-yellow-500" />
            </div>
            <div className="text-3xl font-bold text-white mb-1">${totalRevenue.toFixed(2)}</div>
            <div className="text-sm text-gray-400">Current Page Revenue</div>
          </div>
        </div>

        {/* Pagination */}
        <div className="bg-gray-800 rounded-lg p-4 mb-6 border border-gray-700">
          <div className="flex items-center justify-between">
            <div className="text-gray-400">
              Showing {currentPage * limit + 1} - {Math.min((currentPage + 1) * limit, total)} of {total} subscriptions
            </div>
            <div className="flex items-center gap-4">
              <Button
                onClick={() => setCurrentPage(prev => Math.max(0, prev - 1))}
                disabled={currentPage === 0}
                className="bg-gray-700 hover:bg-gray-600 text-white disabled:opacity-50"
              >
                Previous
              </Button>
              <span className="text-gray-400">
                Page {currentPage + 1} of {totalPages}
              </span>
              <Button
                onClick={() => setCurrentPage(prev => Math.min(totalPages - 1, prev + 1))}
                disabled={currentPage >= totalPages - 1}
                className="bg-gray-700 hover:bg-gray-600 text-white disabled:opacity-50"
              >
                Next
              </Button>
            </div>
          </div>
        </div>

        {/* Subscriptions Table */}
        <div className="bg-gray-800 rounded-lg border border-gray-700 overflow-hidden">
          <table className="w-full">
            <thead className="bg-gray-900">
              <tr>
                <th className="px-6 py-4 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                  User
                </th>
                <th className="px-6 py-4 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                  Plan
                </th>
                <th className="px-6 py-4 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-4 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                  Amount
                </th>
                <th className="px-6 py-4 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                  Period
                </th>
                <th className="px-6 py-4 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                  Created
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-700">
              {subscriptions.map((item) => {
                const sub = item.subscription;
                const user = item.user;
                const isActive = sub.status === 'active';

                return (
                  <tr key={sub.id} className="hover:bg-gray-750">
                    <td className="px-6 py-4">
                      <div>
                        <div className="text-white font-medium">{user?.name || 'Unknown'}</div>
                        <div className="text-sm text-gray-400">{user?.email || 'N/A'}</div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className="text-gray-300 capitalize">{sub.plan || 'Premium'}</span>
                    </td>
                    <td className="px-6 py-4">
                      {isActive ? (
                        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-green-900/30 text-green-400">
                          <CheckCircle className="w-3 h-3" />
                          Active
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-gray-700 text-gray-400">
                          <XCircle className="w-3 h-3" />
                          {sub.status || 'Inactive'}
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <span className="text-white font-medium">
                        ${sub.amount?.toFixed(2) || '0.00'}
                      </span>
                      <span className="text-gray-400 text-sm ml-1">/{sub.currency || 'usd'}</span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm">
                        {sub.current_period_start && (
                          <div className="text-gray-400">
                            Start: {new Date(sub.current_period_start).toLocaleDateString()}
                          </div>
                        )}
                        {sub.current_period_end && (
                          <div className="text-gray-400">
                            End: {new Date(sub.current_period_end).toLocaleDateString()}
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className="text-gray-400 text-sm">
                        {sub.created_at ? new Date(sub.created_at).toLocaleDateString() : 'N/A'}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </main>
    </div>
  );
};

export default AdminSubscriptions;
